import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";

/** Shows the notice-period charge before anything is cancelled. */
export const CancelBooking = ({ booking, onOpenChange, onCancelled }) => {
  const [quote, setQuote] = useState(null);
  const [reason, setReason] = useState("");
  const [waive, setWaive] = useState(false);
  const [waiveReason, setWaiveReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/property/bookings/${booking.booking_ref}/cancellation-quote`)
      .then((r) => setQuote(r.data)).catch((e) => toast.error(apiError(e)));
  }, [booking.booking_ref]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/property/bookings/${booking.booking_ref}/cancel`,
        { reason, waive_charge: waive, waive_reason: waiveReason });
      toast.success(res.data.cancellation_charge > 0
        ? `Cancelled · ${money(res.data.cancellation_charge, booking.currency)} cancellation charge to invoice`
        : "Cancelled with no charge");
      onCancelled?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const cur = booking.currency || "INR";

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-border bg-card" data-testid="cancel-booking-dialog">
        <DialogHeader><DialogTitle>Cancel {booking.booking_ref}</DialogTitle></DialogHeader>
        {!quote ? <div className="py-6 text-center text-[12px] text-muted-foreground">Working out the charge…</div> : (
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-muted/25 p-3 text-[12px]">
              <div className="text-foreground">
                {booking.title} · {booking.space_name}
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground" data-testid="cancel-quote-text">
                {quote.days_notice} days' notice — {quote.band}, so {quote.charge_percent}% of the
                {" "}{money(quote.hire_charge, cur)} hire charge applies.
              </p>
              <div className="mt-2 flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Cancellation charge</span>
                <span className="text-[15px] font-bold text-foreground" data-testid="cancel-quote-amount">
                  {money(waive ? 0 : quote.cancellation_charge, cur)}</span>
              </div>
              {quote.deposit_paid > 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {money(quote.deposit_paid, cur)} deposit already held is offset against it;
                  {" "}{money(quote.refund_due, cur)} would be returned.
                </p>
              )}
            </div>

            <Textarea rows={2} value={reason} data-testid="cancel-booking-reason"
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why is it being cancelled?" className="bg-muted/40 text-[12px]" />

            {quote.cancellation_charge > 0 && (
              <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/25 p-3">
                <div className="text-[11.5px] text-foreground">
                  Waive the cancellation charge
                  <p className="text-[10.5px] text-muted-foreground">
                    Use this for a hirer we have chosen not to charge. It is recorded with your reason.
                  </p>
                </div>
                <Switch checked={waive} onCheckedChange={setWaive} data-testid="cancel-waive-switch" />
              </div>
            )}
            {waive && (
              <Textarea rows={2} value={waiveReason} data-testid="cancel-waive-reason"
                        onChange={(e) => setWaiveReason(e.target.value)}
                        placeholder="Why is the charge being waived? (at least 10 characters)"
                        className="bg-muted/40 text-[12px]" />
            )}

            <Button data-testid="cancel-booking-submit"
                    disabled={busy || (waive && waiveReason.trim().length < 10)}
                    onClick={submit}
                    className="h-9 w-full rounded-full bg-red-600 text-white hover:bg-red-600/90">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Cancel the booking
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
